// Copyright (c) 2024, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font,
} from "@react-pdf/renderer";
import { certificationBadgeLink, signatureMain, signatureSecondary, certificateFooter } from "../../config/constant";
import Background from "../../assets/img/award-background.png";
import PlusJakartaRegular from "../../assets/fonts/PlusJakartaSans-Regular.ttf";
import PlusJakartaExtraBold from "../../assets/fonts/PlusJakartaSans-ExtraBold.ttf";
import PlusJakartaBold from "../../assets/fonts/PlusJakartaSans-Bold.ttf";
import PlusJakartaSemiBold from "../../assets/fonts/PlusJakartaSans-SemiBold.ttf";
import PlusJakartaMedium from "../../assets/fonts/PlusJakartaSans-Medium.ttf";

Font.register({
  family: "PlusJakarta",
  fonts: [
    {
      src: PlusJakartaRegular,
    },
    {
      src: PlusJakartaExtraBold,
      fontWeight: "heavy",
    },
    {
      src: PlusJakartaBold,
      fontWeight: "bold",
    },
    {
      src: PlusJakartaSemiBold,
      fontWeight: "semibold",
    },
    {
      src: PlusJakartaMedium,
      fontWeight: "medium",
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    flexDirection: "row",
    fontFamily: "PlusJakarta",
  },
  section1: {
    width: "100%",
    height: "100%",
    position: "absolute",
    top: 0,
    left: 0,
  },
  section2: {
    padding: 20,
    flexGrow: 1,
  },
  section3: {
    marginTop: 25,
    flexGrow: 1,
  },
  section4: {
    marginTop: 10,
    paddingHorizontal: 50,
    flexGrow: 1
  },
  container: {
    width: "100%",
    height: "100%",
    position: "relative",
  },
  topLogo: {
    marginTop: 20,
    width: 100,
  },
  headingText: {
    color: "#fff",
    marginTop: 10,
    fontSize: 31,
    fontWeight: "extrabold",
  },
  subHeadingText: {
    fontWeight: "medium",
    marginTop: 80,
    color: "#000",
  },
  name: {
    color: "#000",
    paddingBottom: 15,
    fontSize: 30,
    fontWeight: "bold",
  },
  awardedText: {
    fontSize: 17,
    color: "#000",
    fontWeight: "medium",
    width: 380,
  },
  mainHeader: {
    fontSize: 30,
    textTransform: "uppercase",
    color: "#000",
    marginTop: 45,
    fontWeight: "bold",
  },
  secondaryHeader: {
    fontSize: 15,
    textTransform: "uppercase",
    color: "#000",
    fontWeight: "semibold",
  },
  description: {
    fontSize: 12,
    color: "#000",
    fontWeight: "semibold",
  },
  certName: {
    marginTop: 15,
    color: "#000",
    fontSize: 17,
    fontWeight: "bold",
    marginBottom: 50,
    width: 500,
  },
  badge: {
    width: 160,
    top: 40,
    left: 345
  },
  link: {
    textDecoration: "none",
    color: "#000",
  },
  footer: {
    alignSelf: "flex-end",
    flexDirection: "row"
  },
  footerDate: {
    alignSelf: "flex-end",
  },
  footerText: {
    color: "#000",
    fontSize: 12,
    fontWeight: "semibold"
  },
  primaryFooterText: {
    marginLeft: 20,
    marginTop: 10,
    width: 200,
  },
  width200: {
    width: 200
  },
  date: {
    color: "#000",
    fontSize: 12,
    marginLeft: "auto",
    marginRight: "auto",
    width: 200,
    fontWeight: "semibold",
  },
  footerSection: {
    flexDirection: "row",
    fontWeight: "semibold",
    marginTop: 15,
    left: 250
  },
  marginLeft30: {
    marginLeft: 30
  },
  marginTop15: {
    marginTop: 15
  },
  marginTop25: {
    marginTop: 25
  },
  left20: {
    left: -20
  },
  signature: {
    width: 100,
    marginLeft: 20,
    marginTop: 20,
    bottom: -10,
    marginBottom: 20,
  },
  secondarySignature: {
    width: 100,
    marginTop: 20,
    bottom: -10,
    marginBottom: 20,
  },
  background: {
    position: "absolute",
    width: "100%",
    height: "100%",
  },
  midContainer: {
    textAlign: 'center',
    width: '100%',
  }
});

const Award = (props: { certification: any }) => {
  if (!props.certification) {
    return (
      <Document>
        <Page style={styles.page}>
          <Text>Error: No certification data available</Text>
        </Page>
      </Document>
    );
  }

  const { certification } = props;
  const contact = certification.contact || { firstName: "Unknown", lastName: "User" };
  const badgeLink =
    certification.logo ||
    certification.badgeLink ||
    certificationBadgeLink[certification.name as keyof typeof certificationBadgeLink];

  return (
    <Document>
      <Page orientation="landscape" style={styles.page}>
        <View style={styles.container}>
          <View style={styles.section1}>
            <Image src={Background} style={styles.background} />
          </View>
          <View style={styles.midContainer}>
            <View>
              {badgeLink && <Image src={badgeLink} style={styles.badge} />}
              <Text style={styles.mainHeader}>{certification.position || "Award Winner"}</Text>
              {certification.region && certification.region !== "NONE" ? (
                <View style={styles.marginTop15}>
                  <Text style={styles.secondaryHeader}>{certification.region}</Text>
                </View>
              ) : (
                <View style={styles.marginTop25} />
              )}
            </View>
            <View style={styles.section3}>
              <Text style={styles.name}>
                {`${contact.firstName || "Unknown"} ${contact.lastName || "User"}`}
              </Text>
            </View>
            <View style={styles.section4}>
              <Text style={styles.description}>
                Has received this award in recognition of demonstrating outstanding commitment towards
                developing WSO2 product knowledge by receiving multiple WSO2 certifications in{" "}
                {certification.year || "this year"}.
              </Text>
            </View>
          </View>
          <View style={styles.footerSection}>
            <View style={styles.footer}>
              <View>
                <Image src={signatureMain} style={styles.signature} />
                <Text style={[styles.footerText, styles.primaryFooterText]}>
                  {certificateFooter.main.name}
                </Text>
                <Text style={[styles.footerText, styles.marginLeft30]}>
                  {certificateFooter.main.designation}
                </Text>
              </View>
              <View>
                <Image src={signatureSecondary} style={[styles.secondarySignature]} />
                <Text style={[styles.footerText, styles.width200]}>
                  {certificateFooter.secondary.name}
                </Text>
                <Text style={[styles.footerText, styles.left20, styles.width200]}>
                  {certificateFooter.secondary.designation}
                </Text>
              </View>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default Award;
